import { useDeletePackage } from "../hooks/usePackages";
import { Button } from "@shared/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@shared/components/ui/dropdown-menu";
import {
  EyeIcon,
  MoreHorizontal,
  PenIcon,
  Trash2,
  UserCheck,
  Truck,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@features/auth/contexts/AuthContext";
import LivreurAssignmentDialog from "./LivreurAssignmentDialog";
import LivreurStatusUpdateDialog from "./LivreurStatusUpdateDialog";

export function PackagesActionsDropdownMenu({ pkg }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const { mutate, isPending } = useDeletePackage();
  const handleCopy = (value, labal) => {
    navigator.clipboard
      .writeText(value)
      .then(() => toast.success(`${labal} ${t("toast.copiedToClipboard")}`))
      .catch(() => toast.error(`${t("toast.failedToCopy")} ${labal}`));
  };

  return (
    <div className="rtl:pl-2 ltr:pr-2">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" aria-label={t("action.actions")}>
            <MoreHorizontal className="h-12 w-12" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-[220px]">
          <DropdownMenuLabel className="font-semibold text-primary">
            {t("action.actions")}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {/* Copy Actions Group */}
          <div className="px-1 py-1">
            <DropdownMenuItem
              onClick={() => handleCopy(pkg._id, t("packages.id"))}
            >
              <span className="flex items-center gap-2">
                <PenIcon className="h-4 w-4 text-muted-foreground" />
                {t("action.copy", { entity: t("packages.id") })}
              </span>
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() =>
                handleCopy(pkg.customer.phone, t("packages.customerPhone"))
              }
            >
              <span className="flex items-center gap-2">
                <UserCheck className="h-4 w-4 text-muted-foreground" />
                {t("action.copy", { entity: t("packages.customerPhone") })}
              </span>
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() =>
                handleCopy(pkg.trackingNumber, t("packages.trackingNumber"))
              }
            >
              <span className="flex items-center gap-2">
                <Truck className="h-4 w-4 text-muted-foreground" />
                {t("action.copy", { entity: t("packages.trackingNumber") })}
              </span>
            </DropdownMenuItem>
          </div>
          <DropdownMenuSeparator />
          {/* View/Edit Actions Group */}
          <div className="px-1 py-1">
            <DropdownMenuItem
              onClick={() => navigate(`/packages/${pkg._id}/view`)}
            >
              <span className="flex items-center gap-2">
                <EyeIcon className="h-4 w-4 text-muted-foreground" />
                {t("action.view", { type: t("") })}
              </span>
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => navigate(`/packages/${pkg._id}/edit`)}
            >
              <span className="flex items-center gap-2">
                <PenIcon className="h-4 w-4 text-muted-foreground" />
                {t("action.edit", { entity: t("") })}
              </span>
            </DropdownMenuItem>
          </div>
          {/* Livreur Management - Admin Only */}
          {user?.role === "admin" && (
            <>
              <DropdownMenuSeparator />
              <LivreurAssignmentDialog
                packageData={pkg}
                trigger={
                  <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                    <span className="flex items-center gap-2">
                      <UserCheck className="h-4 w-4 text-muted-foreground" />
                      {pkg.livreur
                        ? t("livreur.manageLivreur")
                        : t("livreur.assignLivreur")}
                    </span>
                  </DropdownMenuItem>
                }
              />
            </>
          )}
          {/* Status Update - Livreur Only */}
          {user?.role === "livreur" && pkg.livreur?._id === user?.id && (
            <>
              <DropdownMenuSeparator />
              <LivreurStatusUpdateDialog
                packageData={pkg}
                trigger={
                  <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                    <span className="flex items-center gap-2">
                      <Truck className="h-4 w-4 text-muted-foreground" />
                      {t("Update Status")}
                    </span>
                  </DropdownMenuItem>
                }
              />
            </>
          )}
          <DropdownMenuSeparator />
          {/* Delete Action */}
          <DropdownMenuItem
            disabled={isPending || pkg.status !== "order_received"}
            className="text-red-600 font-semibold hover:bg-red-50 focus:bg-red-100"
            onClick={() => {
              const isConfirmed = confirm(
                t("Are you sure you want to delete this package?")
              );
              if (!isConfirmed) return;
              const toastId = toast.loading(t("deleting package"));
              mutate(pkg._id, {
                onSuccess: () => {
                  toast.success(t("Package deleted"), {
                    id: toastId,
                  });
                  navigate("/packages");
                },
                onError: (error) => {
                  console.log(error);
                  toast.error(`${error.response.data.message}`, {
                    id: toastId,
                  });
                },
              });
            }}
          >
            <span className="flex items-center gap-2">
              <Trash2 className="h-4 w-4" />
              {t("action.delete", { entity: t("") })}
            </span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

const MenuActions = ({ packageId }) => {
  const { mutate, isPending } = useDeletePackage();
  return (
    <div>
      <Button
        disabled={isPending}
        variant="destructive"
        onClick={() => {
          mutate(packageId);
        }}
      >
        <Trash2 /> delete
      </Button>
    </div>
  );
};

export default MenuActions;
